import {
  getCampaignLeadCostQueryOptions,
  useUpdateCampaignMutation,
} from "@/api/campaign.api";
import { creditsQueryOptions } from "@/api/credits.api";
import { LeadScheduleInputs } from "@/components/common/LeadScheduleInputs";
import { Button } from "@/components/generic/Button";
import { getCampaignUpdateCreditStats } from "@/services/credits.service";
import type { Campaign } from "@/types/campaign";
import { queryClient } from "@/utils/api";
import { mergeScheduleUpdates } from "@/utils/campaign-helpers";
import {
  formatWeekPeriod,
  localToUTCDate,
  UTCToLocalDate,
  WEEKDAYS,
  WeekPeriod,
} from "@/utils/dates";
import { zodResolver } from "@hookform/resolvers/zod";
import { setDay } from "date-fns";
import { useEffect, useMemo, useRef } from "react";
import { Form } from "react-aria-components";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { SegmentHeader } from "../basic/SegmentHeader";

export const schedulesSchema = z.object({
  monday: z.number().nullable().default(null),
  tuesday: z.number().nullable().default(null),
  wednesday: z.number().nullable().default(null),
  thursday: z.number().nullable().default(null),
  friday: z.number().nullable().default(null),
});

export const adjustScheduledCampaignWeekSchema = z.object({
  schedules: schedulesSchema.refine((schedules) => {
    const values = Object.values(schedules);
    return values.every(
      (value) => value === null || (value >= 0 && value <= 1000),
    );
  }, "Antallet af leads pr. dag skal være mellem 0 og 1000"),
});

type Props = {
  campaign: Campaign;
  week: WeekPeriod;
  values?: z.infer<typeof schedulesSchema>;
  skipCreditCheck?: boolean;
  autoSubmit?: boolean;
  goToBuyCredits: (data: {
    creditsNeededRestOfPeriod: number;
    creditEstimateRestOfPeriod: number;
    neededCredits: number;
    schedules: z.infer<typeof schedulesSchema>;
  }) => Promise<void>;
  onSuccess: () => Promise<void>;
};

export function AdjustScheduledCampaignWeek({
  campaign,
  week,
  values,
  skipCreditCheck = false,
  autoSubmit = false,
  goToBuyCredits,
  onSuccess,
}: Props) {
  const { schedules, schedulesObject, placeholders } = useMemo(() => {
    const currentSchedulesMap = new Map(
      campaign.schedules.map((schedule) => [
        schedule.dayOfWeek.toLowerCase(),
        schedule,
      ]),
    );

    const schedules = WEEKDAYS.map(({ id }, i) => {
      const currentSchedule = currentSchedulesMap.get(id);

      const dayIndex = (i + 1) % 7;
      const date = setDay(week.start, dayIndex, { weekStartsOn: 1 });
      const scheduleChange = currentSchedule?.scheduleChanges.find(
        (change) =>
          UTCToLocalDate(new Date(change.date)).getTime() === date.getTime(),
      );

      const defaultAmount = currentSchedule?.amount ?? 0;
      const scheduleChangeAmount = scheduleChange?.amount ?? null;

      return {
        dayOfWeek: id,
        defaultAmount,
        scheduleChangeAmount,
        amount: scheduleChangeAmount ?? defaultAmount,
      };
    });

    const schedulesObject = {
      monday: schedules[0],
      tuesday: schedules[1],
      wednesday: schedules[2],
      thursday: schedules[3],
      friday: schedules[4],
    };

    const placeholders = {
      monday: schedulesObject.monday.defaultAmount,
      tuesday: schedulesObject.tuesday.defaultAmount,
      wednesday: schedulesObject.wednesday.defaultAmount,
      thursday: schedulesObject.thursday.defaultAmount,
      friday: schedulesObject.friday.defaultAmount,
    };

    return { schedules, schedulesObject, placeholders };
  }, [campaign, week]);

  const { mutateAsync: updateCampaign, isPending: isUpdatingCampaign } =
    useUpdateCampaignMutation();

  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty, disabled, isSubmitting },
    setValue,
    watch,
  } = useForm({
    disabled: isUpdatingCampaign,
    resolver: zodResolver(adjustScheduledCampaignWeekSchema),
    mode: "onChange",
    defaultValues: {
      schedules: {
        monday: schedulesObject.monday.scheduleChangeAmount,
        tuesday: schedulesObject.tuesday.scheduleChangeAmount,
        wednesday: schedulesObject.wednesday.scheduleChangeAmount,
        thursday: schedulesObject.thursday.scheduleChangeAmount,
        friday: schedulesObject.friday.scheduleChangeAmount,
      },
    },
  });

  useEffect(() => {
    if (values) {
      setValue("schedules", values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const formValues = watch();

  const submit = handleSubmit(async (data) => {
    if (isUpdatingCampaign) return false;

    const newSchedules = schedules.map((schedule, i) => {
      const amount = data.schedules[schedule.dayOfWeek];
      const dayIndex = (i + 1) % 7;
      return {
        dayOfWeek: schedule.dayOfWeek,
        scheduleChanges: [
          {
            date: localToUTCDate(
              setDay(week.start, dayIndex, { weekStartsOn: 1 }),
            ).toISOString(),
            amount,
          },
        ],
      };
    });

    if (!skipCreditCheck) {
      const [credits, campaignLeadCost, updatedCampaignLeadCost] =
        await Promise.all([
          queryClient.ensureQueryData(creditsQueryOptions),
          queryClient.ensureQueryData(
            getCampaignLeadCostQueryOptions(campaign),
          ),
          queryClient.ensureQueryData(
            getCampaignLeadCostQueryOptions({
              ...campaign,
              schedules: mergeScheduleUpdates(campaign.schedules, newSchedules),
            }),
          ),
        ]);

      let isWithinCurrentSubscriptionPeriod = false;

      if (credits.creditsRenewalDate) {
        const creditsRenewalDate = UTCToLocalDate(
          new Date(credits.creditsRenewalDate),
        );
        isWithinCurrentSubscriptionPeriod = week.start < creditsRenewalDate;
      }

      if (isWithinCurrentSubscriptionPeriod) {
        const {
          updatedCreditsNeededRestOfPeriod,
          updatedCreditEstimateRestOfPeriod,
        } = getCampaignUpdateCreditStats({
          credits,
          campaignLeadCost,
          updatedCampaignLeadCost,
        });

        const neededCredits = updatedCreditsNeededRestOfPeriod;

        if (neededCredits) {
          await goToBuyCredits({
            neededCredits,
            creditsNeededRestOfPeriod: updatedCreditsNeededRestOfPeriod,
            creditEstimateRestOfPeriod: updatedCreditEstimateRestOfPeriod,
            schedules: data.schedules,
          });
          return;
        }
      }
    }

    await updateCampaign({ id: campaign.id, schedules: newSchedules });
    await onSuccess();
  });

  const autoSubmittedRef = useRef(false);

  useEffect(() => {
    if (autoSubmittedRef.current) return;

    if (autoSubmit) {
      autoSubmittedRef.current = true;
      submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSubmit]);

  const canReset = useMemo(
    () =>
      WEEKDAYS.some(({ id }) => typeof formValues.schedules[id] === "number"),
    [formValues],
  );

  return (
    <Form
      onSubmit={submit}
      className="mx-auto flex size-full max-w-3xl flex-col items-center justify-center gap-y-10"
    >
      <SegmentHeader
        title={`${campaign.name} | Periode ${formatWeekPeriod(week.start, week.end)}`}
        description="Rediger antallet af leads, der leveres pr. dag indenfor perioden"
      />

      <LeadScheduleInputs
        control={control}
        name="schedules"
        placeholders={placeholders}
      />

      <div className="flex gap-x-4">
        <Button
          size="lg"
          intent="secondary"
          animate
          isDisabled={!canReset || isSubmitting || disabled}
          onPress={() => {
            for (const schedule of schedules) {
              setValue(`schedules.${schedule.dayOfWeek}`, null);
            }
          }}
        >
          Nulstil
        </Button>

        <Button
          size="lg"
          type="submit"
          isDisabled={!isValid || !isDirty || isSubmitting || disabled}
          isPending={isSubmitting}
          animate
        >
          Gem
        </Button>
      </div>
    </Form>
  );
}
